import { useCallback, useEffect, useRef } from 'react';
import { IChartApi, LogicalRange, Time } from 'lightweight-charts';
import { useChartContext } from '../components/chart.context';

const collectCharts = (
  primaryChart: IChartApi | null,
  indicatorCharts: Record<string, IChartApi[]>
) =>
  [primaryChart, ...Object.values(indicatorCharts).flat()].filter(
    Boolean
  ) as IChartApi[];

type HorizontalSyncOptions = {
  /** Attach range-change listeners that mirror zoom/scroll across charts */
  syncOnRangeChange?: boolean;
};

export const useHorizontalScrollSynchronization = (
  options: HorizontalSyncOptions = {}
) => {
  const {
    chartRef,
    indicatorChartRefs,
    chartInitialized,
    subplotChartCreated$,
    data: { indicatorConfigs }
  } = useChartContext();

  const { syncOnRangeChange = true } = options;

  // Avoid event loops: when we set a target chart's range it will emit a change
  // event too, so mark that chart to ignore the immediate echo.
  const ignoreNextByChartRef = useRef<WeakMap<IChartApi, number>>(
    new WeakMap()
  );
  // Track range-change subscriptions so late-created subplot charts get wired
  // up and removed charts are unsubscribed.
  const subscriptionsRef = useRef<Map<IChartApi, () => void>>(new Map());

  const markIgnoreNext = useCallback((chart: IChartApi, count = 1) => {
    // Overwrite instead of stacking counts so we skip only the very next emit.
    ignoreNextByChartRef.current.set(chart, Math.max(1, count));
  }, []);

  const shouldIgnoreNext = useCallback((chart: IChartApi) => {
    const current = ignoreNextByChartRef.current.get(chart) ?? 0;
    if (current <= 0) return false;
    if (current === 1) {
      ignoreNextByChartRef.current.delete(chart);
    } else {
      ignoreNextByChartRef.current.set(chart, current - 1);
    }
    return true;
  }, []);

  const centerChartsOnLogical = useCallback(
    (logicalPosition: number) => {
      // Programmatically center all charts around a logical index (used by crosshair sync)
      const charts = collectCharts(
        chartRef.current,
        indicatorChartRefs.current
      );

      if (!charts.length) return;

      charts.forEach((chart) => {
        const timeScale = chart.timeScale();
        const currentRange = timeScale.getVisibleLogicalRange();
        const span =
          currentRange === null ? 200 : currentRange.to - currentRange.from;
        const halfSpan = span / 2;

        markIgnoreNext(chart);
        timeScale.setVisibleLogicalRange({
          from: logicalPosition - halfSpan,
          to: logicalPosition + halfSpan
        });
      });
    },
    [chartRef, indicatorChartRefs, markIgnoreNext]
  );

  const centerChartsOnTime = useCallback(
    (time: Time) => {
      const mainChart = chartRef.current;
      if (!mainChart) return;

      const timeScale = mainChart.timeScale();
      const coordinate = timeScale.timeToCoordinate(time);
      if (coordinate === null) return;

      const logical = timeScale.coordinateToLogical(coordinate);
      if (logical === null) return;

      centerChartsOnLogical(logical as number);
    },
    [chartRef, centerChartsOnLogical]
  );

  useEffect(() => {
    // Bail out until the charts exist or when caller opted out of range listeners.
    if (!chartInitialized || !syncOnRangeChange) return;

    // This is just for syncing main and subplot charts
    const ensureSubscriptions = () => {
      // We don't know when subplots are mounted, so scan live refs and wire any newcomers.
      const charts = collectCharts(
        chartRef.current,
        indicatorChartRefs.current
      );
      if (!charts.length) return;

      // Subscribe new charts
      charts.forEach((chart) => {
        if (subscriptionsRef.current.has(chart)) return;

        const handleRangeChange = (range: LogicalRange | null) => {
          if (!range) return;
          if (shouldIgnoreNext(chart)) return;

          // Mirror the range to all siblings in this timeframe group.
          const peers = collectCharts(
            chartRef.current,
            indicatorChartRefs.current
          );

          peers.forEach((otherChart) => {
            if (otherChart === chart) return;
            markIgnoreNext(otherChart);
            otherChart.timeScale().setVisibleLogicalRange({
              from: range.from,
              to: range.to
            });
          });
        };

        const timeScale = chart.timeScale();
        timeScale.subscribeVisibleLogicalRangeChange(handleRangeChange);
        subscriptionsRef.current.set(chart, () =>
          timeScale.unsubscribeVisibleLogicalRangeChange(handleRangeChange)
        );
      });

      // Unsubscribe charts that no longer exist
      subscriptionsRef.current.forEach((unsubscribe, chart) => {
        if (charts.includes(chart)) return;
        unsubscribe();
        subscriptionsRef.current.delete(chart);
      });
    };

    ensureSubscriptions();

    // Subplot charts are created lazily. Re-run subscription wiring whenever
    // a subplot chart creation is signaled.
    const retryTimeouts = [50, 150, 300];
    const timers = retryTimeouts.map((ms) =>
      setTimeout(() => ensureSubscriptions(), ms)
    );
    const subplotChartCreatedSubscription = subplotChartCreated$.subscribe(
      () => {
        ensureSubscriptions();
      }
    );

    return () => {
      timers.forEach(clearTimeout);
      subplotChartCreatedSubscription.unsubscribe();
      subscriptionsRef.current.forEach((unsubscribe) => unsubscribe());
      subscriptionsRef.current.clear();
    };
  }, [
    chartInitialized,
    indicatorConfigs.subplot.length,
    markIgnoreNext,
    subplotChartCreated$,
    shouldIgnoreNext,
    syncOnRangeChange
  ]);

  return {
    centerChartsOnLogical,
    centerChartsOnTime
  };
};
